import { Box, type SxProps, type Theme } from '@mui/material';
import {
  EditorContent,
  SlashCommandMenu,
  useEditor,
  useSlashCommand,
} from '@provision/rich-text-editor';
import { useEffect, useRef, useState } from 'react';
import { EditableTitle } from './editor/editable-title';
import { FormatBar } from './editor/format-bar';
import {
  buildLegalDocumentExtensions,
  HIDDEN_HEADING_1,
  TABLE_SLASH_ITEM,
} from './editor/legal-document-extensions';
import { LinkPopover } from './editor/link-popover';
import { TableBar } from './editor/table-bar';
import { LegalDocumentLayout } from './legal-document-layout';

interface LegalDocumentEditorProps {
  title: string;
  onTitleChange: (title: string) => void;
  /** Read when the editor mounts; key the component to load another document. */
  bodyHtml: string;
  onBodyChange: (bodyHtml: string) => void;
  updatedAt: string | null;
  placeholder?: string;
}

const SLASH_COMMAND_OPTIONS = { additionalItems: [HIDDEN_HEADING_1, TABLE_SLASH_ITEM] };

const editorSx: SxProps<Theme> = {
  '& .ProseMirror': { outline: 'none', minHeight: '20rem' },
  '& .ProseMirror p.is-editor-empty:first-of-type::before': {
    content: 'attr(data-placeholder)',
    color: 'text.disabled',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
};

export const LegalDocumentEditor = ({
  title,
  onTitleChange,
  bodyHtml,
  onBodyChange,
  updatedAt,
  placeholder = 'Write the document. Type / for headings and lists.',
}: LegalDocumentEditorProps) => {
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const { extension: slashExtension, controller: slashCommand } =
    useSlashCommand(SLASH_COMMAND_OPTIONS);

  const onBodyChangeRef = useRef(onBodyChange);

  useEffect(() => {
    onBodyChangeRef.current = onBodyChange;
  }, [onBodyChange]);

  const editor = useEditor(
    {
      extensions: buildLegalDocumentExtensions({ placeholder, slashExtension }),
      content: bodyHtml,
      shouldRerenderOnTransaction: true,
      onUpdate: ({ editor: updated }) => onBodyChangeRef.current(updated.getHTML()),
    },
    [slashExtension],
  );

  return (
    <LegalDocumentLayout
      title={<EditableTitle value={title} onChange={onTitleChange} />}
      updatedAt={updatedAt}
    >
      <Box sx={editorSx}>
        <EditorContent editor={editor} />
      </Box>
      {editor ? (
        <>
          <FormatBar editor={editor} onLinkClick={() => setIsLinkPopoverOpen(true)} />
          <TableBar editor={editor} />
          <LinkPopover
            editor={editor}
            open={isLinkPopoverOpen}
            onClose={() => setIsLinkPopoverOpen(false)}
          />
        </>
      ) : null}
      <SlashCommandMenu
        open={slashCommand.state.isOpen}
        anchorEl={slashCommand.state.anchorEl}
        items={slashCommand.state.items}
        selectedIndex={slashCommand.state.selectedIndex}
        onSelect={slashCommand.selectItem}
        onHighlight={slashCommand.setSelectedIndex}
        onClose={slashCommand.close}
      />
    </LegalDocumentLayout>
  );
};
